import { useState } from 'react';
import { Modal, ModalFooter } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';
import type { GenerateRefreshInput } from '../../api/content';

export type RegenerationMode = 'normal' | 'notion_patch' | 'seo_geo_optimize';

export interface RegenerateDialogProps {
  isOpen: boolean;
  itemTitle: string;
  isPending?: boolean;
  onClose: () => void;
  onConfirm: (input: Pick<GenerateRefreshInput, 'regenerationMode' | 'useSerpAnalysis'>) => void;
}

export function RegenerateDialog({ isOpen, itemTitle, isPending, onClose, onConfirm }: RegenerateDialogProps) {
  const [mode, setMode] = useState<RegenerationMode>('notion_patch');
  const [useSerp, setUseSerp] = useState(true);

  function handleConfirm() {
    onConfirm({
      regenerationMode: mode,
      useSerpAnalysis: mode === 'normal' ? useSerp : mode === 'seo_geo_optimize',
    });
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Re-generate page" description={itemTitle} size="lg">
      <div className="space-y-4">
        <fieldset className="space-y-3">
          <legend className="text-body-sm font-medium text-text-primary mb-1">Mode</legend>

          <label className="flex gap-3 rounded-2xl border border-border p-3 cursor-pointer hover:border-border-strong">
            <input
              type="radio"
              name="regen-mode"
              value="notion_patch"
              checked={mode === 'notion_patch'}
              onChange={() => setMode('notion_patch')}
              className="mt-1"
            />
            <div className="space-y-1">
              <div className="text-body-sm font-medium text-text-primary">Update from Notion only</div>
              <div className="text-body-xs text-text-tertiary">
                Safe update. Preserves existing copy, only patches sections affected by Notion source or keyword changes. Skips SERP and research. Cheap.
              </div>
            </div>
          </label>

          <label className="flex gap-3 rounded-2xl border border-border p-3 cursor-pointer hover:border-border-strong">
            <input
              type="radio"
              name="regen-mode"
              value="seo_geo_optimize"
              checked={mode === 'seo_geo_optimize'}
              onChange={() => setMode('seo_geo_optimize')}
              className="mt-1"
            />
            <div className="space-y-1">
              <div className="text-body-sm font-medium text-text-primary">Optimize for SEO and GEO</div>
              <div className="text-body-xs text-text-tertiary">
                Keeps the page mostly intact, but improves keyword coverage, entity coverage, answer-first phrasing, internal links, and other high-impact ranking signals. Always re-runs research and SERP.
              </div>
            </div>
          </label>

          <label className="flex gap-3 rounded-2xl border border-border p-3 cursor-pointer hover:border-border-strong">
            <input
              type="radio"
              name="regen-mode"
              value="normal"
              checked={mode === 'normal'}
              onChange={() => setMode('normal')}
              className="mt-1"
            />
            <div className="space-y-1">
              <div className="text-body-sm font-medium text-text-primary">Complete rewrite</div>
              <div className="text-body-xs text-text-tertiary">
                Full LLM rewrite over the current draft using Notion + research + SERP. May change a lot of copy.
              </div>
            </div>
          </label>
        </fieldset>

        {mode === 'normal' && (
          <label className="flex items-center gap-2 text-body-xs text-text-secondary select-none">
            <input
              type="checkbox"
              checked={useSerp}
              onChange={(e) => setUseSerp(e.target.checked)}
              className="rounded border-border"
            />
            Re-run SERP analysis (refreshes competitor entities for the Semantic score)
          </label>
        )}

        {mode === 'seo_geo_optimize' && (
          <Alert variant="info">
            <span className="text-body-xs">
              This mode preserves the page direction and most copy, then makes targeted SEO/GEO improvements using the Website Content brief, SERP analysis, and semantic coverage checks.
            </span>
          </Alert>
        )}

        {mode === 'normal' && (
          <Alert variant="warning">
            <span className="text-body-xs">
              This will rewrite the current draft. Manual edits may be lost — the previous version is preserved in history.
            </span>
          </Alert>
        )}
      </div>

      <ModalFooter>
        <Button size="sm" variant="ghost" onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button size="sm" variant="primary" onClick={handleConfirm} isLoading={isPending}>
          Start re-generation
        </Button>
      </ModalFooter>
    </Modal>
  );
}
